import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError, finalize, switchMap } from 'rxjs/operators';
import { ToasterService } from '../services/core/toaster.service';
import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { CoreService } from '../services/core/core.service';
import { UserService } from '../services/core/user.service';
import { Router } from '@angular/router';
import { TwoFactorPromptService } from '../services/core/two-factor-prompt.service';
import { DialogService } from '../services/core/dialog.service';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  showSpinner: any = [];
  constructor(
    private toasterService: ToasterService,
    private spinner: NgxSpinnerService,
    private coreService: CoreService,
    private currentUser: UserService,
    private router: Router,
    private twoFactorPrompt: TwoFactorPromptService,
    private dialogService: DialogService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    //console.log('request', request?.body?.get('loadingService'));
    if (request.method == 'PATCH' || request.method == 'POST') {
      let shouldShowSpinner = true;

      // Check if body is FormData
      if (request.body instanceof FormData) {
        // For FormData, check using get() method
        const loadingService = request.body.get('loadingService');
        if (loadingService === 'false') {
          shouldShowSpinner = false;
        }
      } else {
        // For regular JSON objects, check property directly
        // @ts-ignore
        if (request?.body?.loadingService === false) {
          shouldShowSpinner = false;
        }
      }

      if (shouldShowSpinner) {
        // this.showSpinner.push('loading in progress');
        // if (this.showSpinner.length > 0) {
        //   this.spinner.show();
        // }
      }
    }

    if (
      (request.method == 'GET' && !request.params.has('loadingService')) ||
      (request.params.has('loadingService') &&
        request.params.get('loadingService') == 'true')
    ) {
      this.showSpinner.push('loading in progress');

      if (this.showSpinner.length > 0) {
        // this.spinner.show();
      }
    }

    if (request.body) {
      // Handle FormData separately
      if (request.body instanceof FormData) {
        // For FormData, check if loadingService exists and remove it
        if (request.body.has('loadingService')) {
          const updatedFormData = new FormData();
          // Copy all entries except loadingService
          request.body.forEach((value, key) => {
            if (key !== 'loadingService') {
              updatedFormData.append(key, value);
            }
          });

          const updatedRequest = request.clone({
            body: updatedFormData,
          });
          request = updatedRequest;
        }
      } else {
        // Handle regular JSON objects
        if (request.body.loadingService !== undefined) {
          // Clone the request and remove the 'loadingService' key from the body
          const updatedBody = { ...request.body };
          delete updatedBody.loadingService;

          const updatedRequest = request.clone({
            body: updatedBody,
          });

          // Continue with the updated request
          request = updatedRequest;
        }
      }
    }

    if (
      request.method == 'GET' &&
      request.params.get('loadingService') !== undefined
    ) {
      // Clone the request and remove the 'loadingService' key from the body

      const updateRequest = request.clone({
        params: request.params.delete('loadingService'),
      });

      // Continue with the updated request
      request = updateRequest;
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // 2FA required — prompt user and replay the request with the code
        if (error.status === 422 && error.error?.two_factor_code_required) {
          return this.handle2fa(request, next);
        }

        // Expired/revoked token: UnauthorizedInterceptor already signed the user
        // out and toasted, so stay quiet here. Auth endpoints are excluded — a
        // failed login is also a 401 and still needs its "Invalid credentials".
        const isAuthEndpoint =
          /\/(login|auth\/impersonate|forgot-password|reset-password)/.test(request.url);
        if (error.status === 401 && !isAuthEndpoint) {
          return throwError(() => error);
        }

        console.log('handle', error);
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
          // client-side error
          this.toasterService.showErrorMessage(
            'Something went wrong. Please try again.',
          );
          return throwError(() => error);
        } else {
          if (error.status === 0) {
            this.toasterService.showErrorMessage(
              'Something went wrong. Please try again.',
            );
            return throwError(() => error);
          }

          // Server-side failures should always show a clear generic message
          if (
            error.status === 500 ||
            error.status === 502 ||
            error.status === 503 ||
            error.status === 504
          ) {
            this.toasterService.showErrorMessage(
              'Something went wrong. Please try again.',
            );
            return throwError(() => error);
          }

          // Route-not-found 404s: never echo Laravel's raw
          // "The route api/v1/... could not be found." message —
          // it leaks the internal URL into the toast. Genuine
          // resource 404s (with a meaningful controller message)
          // still fall through to the normal handling below.
          if (error.status === 404) {
            // PDF link signing is optional: PdfLinkService.sign() already falls back to the unsigned
            // link when the backend has no `pdf/sign` route, so no toast for that expected 404.
            if (request.url.includes('pdf/sign')) {
              return throwError(() => error);
            }
            const rawMsg = (error.error?.message ?? '').toString();
            if (
              /^The route\b/i.test(rawMsg) ||
              /could not be found/i.test(rawMsg)
            ) {
              this.toasterService.showErrorMessage(
                'The requested action is not available right now.',
              );
              return throwError(() => error);
            }

            // Benign lookup: the event-folder endpoint 404s when a record has no
            // "save_image" folder yet. Every caller (jobsite gallery / attachments)
            // already handles this 404 by showing an empty/preparing state, so the
            // global toast is redundant noise — suppress it and let them handle it.
            if (request.url.includes('document/event-folder')) {
              return throwError(() => error);
            }
          }

          let displayErrorMessages = error.error?.errors;

          // Safe-access: not every backend error response
          // includes a `data` envelope (e.g. control-flow
          // sentinel errors that carry just `code` +
          // `message`). Crashing here would clobber the
          // body and turn a structured error into the
          // literal string 'Server Error' downstream.
          if (displayErrorMessages == undefined) {
            displayErrorMessages = error.error?.data?.errors;
          }

          // If still undefined, check if there's a direct message
          if (displayErrorMessages == undefined && error.error?.message) {
            displayErrorMessages = error.error.message;
          }

          console.log('displayErrorMessages 1', displayErrorMessages);

          // server-side error
          const isArray = Array.isArray(displayErrorMessages);
          console.log('displayErrorMessages isArray', isArray);
          const objectKeys = displayErrorMessages
            ? Object.keys(displayErrorMessages)
            : [];
          console.log('displayErrorMessages objectKeys', objectKeys);
          const isObject = objectKeys.length;
          console.log('displayErrorMessages objectKeys', objectKeys);
          if (isArray) {
            const errorsLen = Object.keys(displayErrorMessages).length;
            if (errorsLen) {
              Object.keys(displayErrorMessages).forEach((item, i) => {
                if (i < 2) {
                  this.toasterService.showError(
                    `Error in : ${item}`,
                    `${displayErrorMessages[item][0]}`,
                  );
                }
              });
            }
          } else if (this.coreService.isString(displayErrorMessages)) {
            if (displayErrorMessages === 'Access denied') {
              // Dialogs are attached to the CDK overlay container on
              // document.body, not under <router-outlet>, so the redirect
              // below would leave them open over the login page.
              this.dialogService.closeAll();
              this.currentUser.flushAll();
              this.router.navigate(['/auth/login']);
            }
            console.log('displayErrorMessages', displayErrorMessages);
            this.toasterService.showError('Oops!', displayErrorMessages);
          } else if (isObject) {
            Object.keys(displayErrorMessages).forEach((item, i) => {
              if (i < 2) {
                const originalMessage = displayErrorMessages[item][0] || '';
                const cleanedMessage = originalMessage
                  .toString()
                  .replace(/\s*\bid\b\s*/gi, ' ')
                  .trim();
                this.toasterService.showWarning(cleanedMessage);
              }
            });
          } else {
            if (error.error?.errors) {
              const errors = displayErrorMessages.toString().slice(0, 70);
              this.toasterService.showError(
                `Error Code: ${error.status}`,
                errors,
              );
            } else if (error.error?.data?.message) {
              this.toasterService.showWarning(error.error.data.message);
            } else if (error.error?.code) {
              // Body uses the control-flow sentinel
              // shape ({ success, code, message, ... }).
              // Suppress the global toaster — the
              // calling component handles these by
              // branching on `code` (e.g. open a
              // dedicated dialog).
            } else {
              this.toasterService.showError(
                `Error Code: ${error.status}`,
                `${error.error?.message}`,
              );
            }
          }
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (request.method == 'PATCH' || request.method == 'POST') {
          let shouldPopSpinner = true;

          // Check if body is FormData
          if (request.body instanceof FormData) {
            // For FormData, check using get() method
            const loadingService = request.body.get('loadingService');
            if (loadingService === 'false') {
              shouldPopSpinner = false;
            }
          } else {
            // For regular JSON objects, check property directly
            // @ts-ignore
            if (request?.body?.loadingService === false) {
              shouldPopSpinner = false;
            }
          }

          if (shouldPopSpinner) {
            // this.showSpinner.pop();
          }
        }

        if (
          (request.method == 'GET' && !request.params.has('loadingService')) ||
          (request.params.has('loadingService') &&
            request.params.get('loadingService') == 'true')
        ) {
          // this.showSpinner.pop();
        }

        // if(request?.body?.loadingService == undefined ||  request?.body?.loadingService) {
        //   console.log('request?.body?.loadingService pop 1', this.showSpinner);
        //   this.showSpinner.pop();
        // }
        //
        // if(request?.params?.get('loadingService') == undefined ||  request?.params?.has('loadingService')) {
        //   console.log('request?.body?.loadingService pop 2', this.showSpinner);
        //   this.showSpinner.pop();
        // }
        //

        // console.log('request?.body?.loadingService length', this.showSpinner);
        if (this.showSpinner.length == 0) {
          // this.spinner.hide();
        }
      }),
    );
  }

  /**
   * Handles 422 responses with two_factor_code_required flag.
   * Opens a 2FA prompt dialog, then replays the original request
   * with the X-2FA-Code header attached.
   */
  private handle2fa(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.twoFactorPrompt.prompt().pipe(
      switchMap((code: string | null) => {
        if (!code) {
          // User cancelled — propagate a clean error
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 422,
                error: { success: false, message: 'Action cancelled.' },
              }),
          );
        }
        // Replay original request with the 2FA code header
        const retried = request.clone({
          setHeaders: { 'X-2FA-Code': code },
        });
        return next.handle(retried);
      }),
    );
  }
}
